import { useEffect, useState } from 'react';

import { soggetti as apiSoggetti } from '../api/scadenziario.ts';
import type { Soggetto } from '../api/tipi-dati.ts';

/**
 * Scelta del soggetto.
 *
 * Non è un menu a tendina: dipendenti, attrezzature e sedi insieme possono
 * essere parecchi, e cercare per nome o matricola è più rapido che scorrere
 * un elenco. Una volta scelto si mostra la sola riga selezionata, con
 * «Cambia» per riaprire la ricerca.
 *
 * Chiede solo i soggetti attivi: quelli disattivati non possono ricevere
 * nuove scadenze e il backend li rifiuterebbe comunque.
 */
export function SelettoreSoggetto({
  selezionato,
  onSeleziona,
}: {
  selezionato: Soggetto | null;
  onSeleziona: (soggetto: Soggetto) => void;
}) {
  const [inRicerca, setInRicerca] = useState(selezionato === null);
  const [testo, setTesto] = useState('');
  const [risultati, setRisultati] = useState<Soggetto[]>([]);
  const [inCaricamento, setInCaricamento] = useState(false);

  useEffect(() => {
    if (!inRicerca) return;

    let annullato = false;
    setInCaricamento(true);

    // Piccola attesa prima di interrogare il server: senza, ogni tasto
    // digitato genera una richiesta e le risposte possono arrivare fuori
    // ordine, facendo lampeggiare risultati vecchi.
    const attesa = setTimeout(() => {
      apiSoggetti
        .elenca({ attivo: 'true', ...(testo.trim() !== '' ? { cerca: testo.trim() } : {}), limite: 25 })
        .then((r) => { if (!annullato) setRisultati(r.elementi); })
        .catch(() => { if (!annullato) setRisultati([]); })
        .finally(() => { if (!annullato) setInCaricamento(false); });
    }, 250);

    return () => { annullato = true; clearTimeout(attesa); };
  }, [testo, inRicerca]);

  if (!inRicerca && selezionato !== null) {
    return (
      <div className="soggetto-scelto">
        <span>
          <strong>{selezionato.denominazione}</strong>
          {selezionato.codice !== null && <span className="tenue"> {selezionato.codice}</span>}
          {selezionato.reparto !== null && <span className="tenue"> {selezionato.reparto}</span>}
        </span>
        <button type="button" className="bottone-piatto" onClick={() => setInRicerca(true)}>
          Cambia
        </button>
      </div>
    );
  }

  return (
    <div className="selettore">
      <input
        type="text"
        value={testo}
        onChange={(e) => setTesto(e.target.value)}
        placeholder="Cerca fra dipendenti, attrezzature, sedi…"
        aria-label="Cerca un soggetto"
        autoFocus
      />

      <ul className="risultati" role="listbox">
        {inCaricamento && <li className="tenue">Ricerca…</li>}

        {!inCaricamento && risultati.length === 0 && (
          <li className="tenue">Nessun soggetto trovato.</li>
        )}

        {risultati.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              role="option"
              aria-selected={selezionato?.id === s.id}
              onClick={() => { onSeleziona(s); setInRicerca(false); setTesto(''); }}
            >
              <strong>{s.denominazione}</strong>
              {s.codice !== null && <span className="tenue"> · {s.codice}</span>}
              {s.reparto !== null && <span className="tenue"> · {s.reparto}</span>}
            </button>
          </li>
        ))}
      </ul>

      {selezionato !== null && (
        <button type="button" className="bottone-piatto" onClick={() => setInRicerca(false)}>
          Torna indietro senza cambiare
        </button>
      )}
    </div>
  );
}
